import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory } from 'react-router-dom';

import { find, first, get, isEmpty, isNil, toNumber } from 'lodash';
import { getStandards, useAssignmentSectionsList } from 'sdk';

import { TopBarSelect, useNavbar } from 'pages/Teachers/shared';
import { buildSectionsOptions } from 'pages/Teachers/shared/utils';
import { buildFilters } from 'utils/filters';
import { notifyError } from 'utils/notifications';
import { useFetch } from 'utils/sdk';
import { parseParams } from 'utils/urls';

import { PreviousLocationContext } from './utils';

export const useStandards = (selectedStandardsIds) => {
  const [standardSearchValue, setStandardSearchValue] = useState('');

  const params = useMemo(
    () =>
      buildFilters({
        search: standardSearchValue,
        'exclude_standards[]': selectedStandardsIds
      }),
    [standardSearchValue, selectedStandardsIds]
  );

  const {
    data: { results: standards },
    loading: standardsLoading
  } = useFetch(getStandards, { data: { results: [] } }, params);

  return {
    standards,
    setStandardSearchValue,
    standardSearchValue,
    standardsLoading
  };
};

export const useStandardsV2 = (
  selectedStandardsIds,
  anscestorId,
  isMathAssignment
) => {
  const [standardSearchValue, setStandardSearchValue] = useState('');
  const params = useMemo(
    () =>
      buildFilters({
        search: standardSearchValue,
        'exclude_standards[]': selectedStandardsIds,
        ancestor_id: anscestorId,
        math_standards: isMathAssignment
      }),
    [standardSearchValue, selectedStandardsIds, anscestorId, isMathAssignment]
  );

  const {
    data: { results: standards },
    loading: standardsLoading
  } = useFetch(getStandards, { data: { results: [] } }, params);

  return {
    standards,
    setStandardSearchValue,
    standardSearchValue,
    standardsLoading
  };
};

export const usePreviousLocation = () => {
  return useContext(PreviousLocationContext);
};

export const useSections = (trackerId) => {
  const history = useHistory();
  const [selectedSection, setSelectedSection] = useState(null);

  const { data: sections, isLoading: loading } = useAssignmentSectionsList({
    trackerId
  });

  useEffect(() => {
    if (!loading && !isNil(sections)) {
      // TODO: Handle errors in useFetch in order to be able to show them here
      if (isEmpty(sections)) {
        notifyError('There are no active sections for this assignment.');
      }
    }
  }, [loading, sections]);

  useEffect(() => {
    if (!isEmpty(sections) && !loading && !selectedSection) {
      const searchParams = parseParams(history.location.search);
      const sectionIdFromParams =
        get(searchParams, 'section') &&
        find(sections, { id: toNumber(searchParams.section) });

      setSelectedSection(sectionIdFromParams || first(sections));
    }
  }, [sections, loading, selectedSection, history]);

  useEffect(() => {
    setSelectedSection(null);
  }, [trackerId]);

  return sections || [];
};

export const useNavigation = ({
  assignment,
  selectedSectionId,
  sections,
  selectSection
}) => {
  const { navigation } = useNavbar({ title: '' });

  useEffect(() => {
    if (assignment) {
      navigation.setTitle(get(assignment, 'tracker_name'));
    } else {
      navigation.setTitle('');
    }
  }, [navigation, assignment]);

  const renderValue = useCallback(
    (value) => {
      return get(find(sections, { id: value }), 'section_name', '');
    },
    [sections]
  );

  useEffect(() => {
    if (!isEmpty(sections) && selectedSectionId) {
      navigation.setLeftSubheader(
        <TopBarSelect
          value={selectedSectionId}
          renderValue={renderValue}
          onChange={selectSection}
          options={buildSectionsOptions(sections)}
        />
      );
    }
  }, [navigation, selectedSectionId, sections, selectSection, renderValue]);
};
